import { useState } from 'react';
import {
  fetchTestsByText,
  fetchTestsByTags,
  fetchTestsPlansByBuild,
  fetchTestsByTestTool,
  fetchPlansByCodestream,
  fetchPlansBySquad,
  fetchGeneratedTestPlansByCodestream,
  fetchCatalogEntry,
  fetchAISuggestion,
} from '../api/services';

export function useFetchTestsByText() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchTests(text = []) {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const queryString = Array.isArray(text) ? text.join(',') : text;
      const data = await fetchTestsByText(queryString);
      setTests(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearTests() {
    setTests([]);
  }

  return { tests, loading, showLoadingMessage, error, fetchTests, clearTests };
}

export function useFetchTestsByTestTool() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchTests(text = []) {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const queryString = Array.isArray(text) ? text.join(',') : text;
      const data = await fetchTestsByTestTool(queryString);
      setTests(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearTests() {
    setTests([]);
  }

  return { tests, loading, showLoadingMessage, error, fetchTests, clearTests };
}

export function useFetchTestsByTags() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchTests(tags = []) {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const queryString = Array.isArray(tags) ? tags.join(',') : tags;
      console.log(queryString);
      const data = await fetchTestsByTags(queryString);
      setTests(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearTests() {
    setTests([]);
  }

  return { tests, loading, showLoadingMessage, error, fetchTests, clearTests };
}

export function useFetchTestsPlansByBuild() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchPlans(build = '') {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const data = await fetchTestsPlansByBuild(build);
      setPlans(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearPlans() {
    setPlans([]);
  }

  return { plans, loading, showLoadingMessage, error, fetchPlans, clearPlans };
}

/**
 * Hook to fetch test plans filtered by codestream version.
 */
export function useFetchPlansByCodestream() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchPlans(codestream = '') {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const data = await fetchPlansByCodestream(codestream);
      setPlans(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearPlans() {
    setPlans([]);
  }

  return {
    plans,
    loading,
    showLoadingMessage,
    error,
    fetchPlans,
    clearPlans,
  };
}

/**
 * Hook to fetch test plans filtered by squad.
 */
export function useFetchPlansBySquad() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchPlans(squad = '') {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const data = await fetchPlansBySquad(squad);
      setPlans(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearPlans() {
    setPlans([]);
  }

  return {
    plans,
    loading,
    showLoadingMessage,
    error,
    fetchPlans,
    clearPlans,
  };
}

export function useGenerateTestPlansByCodestream() {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [error, setError] = useState(null);

  async function fetchPlans(codestream = '') {
    setLoading(true);
    setShowLoadingMessage(true);
    setError(null);

    try {
      const data = await fetchGeneratedTestPlansByCodestream(codestream);
      setPlans(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
      setTimeout(() => setShowLoadingMessage(false), 500);
    }
  }

  function clearPlans() {
    setPlans({});
  }

  return {
    plans,
    loading,
    showLoadingMessage,
    error,
    fetchPlans,
    clearPlans,
  };
}

export function useFetchCatalogEntry() {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function fetchEntry(testName) {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchCatalogEntry(testName);
      setEntry(data);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  function clearEntry() {
    setEntry(null);
  }

  return {
    entry,
    loading,
    error,
    fetchEntry,
    clearEntry,
  };
}

export function useAISuggestion() {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function askAI(featureDescription) {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchAISuggestion(featureDescription);
      setSummary(result);
    } catch (e) {
      setError(e.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  function clearSummary() {
    setSummary('');
  }

  return {
    summary,
    loading,
    error,
    askAI,
    clearSummary,
  };
}
